import { navigate } from 'gatsby';
import getStateField, { getStateCode } from '~state/getStateField';
import { store } from '~state/ReduxWrapper';
import {
  completeAbsenteeForIneligibleState,
  sendAbsenteeForm,
} from '~components/Absentee/postFinishEvents';

export const printMailDisabled = ['KY', 'MS', 'SC']; // self-print AKA print_mail is turned off for these states

// these states have online portals but do not allow submission of applications from a 3rd party
export const onlinePortalNo3rdPartyPrint = ['KY', 'SC'];

// these states have online portals but do not allow online submission & require the application be printed
export const onlinePortalMustPrintApplication = ['SC'];

// These states have universal vote-by-mail but do not have a concept of a one-time absentee ballot for those
// who are away from home temporarily. They should be directed to their LEO if their address has not changed permanently
export const universalVbmNoTemporary = ['CO'];

// In these states, at least some regions have their own ballot request URLs
export const hasRegionalBallotRequestUrls = ['FL', 'IL', 'NY'];

/**
 * This navigates to the appropriate "second step" of Absentee -- the screen
 * the user should see after submitting their basic biographical information.
 *
 * skipVBMUniversal: if we've already told the user about OVBM, and they want
 *   to print and mail anyway, pass true
 */
export default function navigateToSecondStep({
  skipVBMUniversal = false,
  skipPAVOption = false,
} = {}) {
  const vbmUniversal = getStateField('has_automatic_vbm');
  const absenteeAccessLevel = getStateField('absentee_access_levels');
  const stateCode = getStateCode();
  const { region, ovbmLink } = store.getState().absenteeFields;

  /*
    The routing logic here need context:
    - First, if a state is universal VBM, send to that page. We'll handle situations where the state no
      longer allows VBM applications on that page. We'll also let people choose on that page to submit an
      application for a one-time ballot.
    - Second, if we don't know the user's region (because we were not able to geocode it from their
      address), ask them for it.
    - Third, if the user lives in a permanent absentee voting state and hasn't already declined the option,
      ask them whether they want to sign up for PAV.
    - Fourth, users who have an ovbmLink will be sent to submit-method to choose their submission method
      (online or self-print).
    - Fifth, we want to direct users who are from ineligible states. They can't take additional
      action using our tools.
    - Lastly, for users who only have print_mail as an option, we should trigger the sendAbsenteeForm() function.
      This will trigger the backend to send the form, then redirect the user to the download-pdf page.
  */

  if (vbmUniversal && !skipVBMUniversal) {
    navigate('/tools/absentee/universal-vbm/');
  } else if (!region) {
    navigate('/tools/absentee/select-region/');
  } else if (absenteeAccessLevel === 'permanent' && !skipPAVOption) {
    navigate('/tools/absentee/pav-or-one-time');
  } else if (ovbmLink) {
    navigate('/tools/absentee/submit-method/');
  } else if (printMailDisabled.includes(stateCode)) {
    completeAbsenteeForIneligibleState();
  } else {
    sendAbsenteeForm();
  }
}

/**
 * This handles the result of POSTing a new Absentee record to the backend. It
 * stores the appropriate fields of the result in Redux, then calls
 * navigateToSecondStep() to navigate the user to the next step of the
 * process.
 */
export function handleAbsenteePostResult(
  postValue,
  postResult,
  referringTool = null
) {
  const absenteeFieldsStateUpdate = {
    uuid: postResult.uuid || '',
    action_id: postResult.action_id || '',
    sms_opt_in_subscriber: postValue.sms_opt_in_subscriber,
    sms_opt_in_voteamerica: postValue.sms_opt_in_voteamerica,
    esignMethod: postResult.esign_method,
    formRequested: false,
    ovbmLink: postResult.ovbm_link,
    region: postResult.region,
    referring_tool: referringTool,
  };

  if (postResult.regions) {
    const possibleRegionsRemap = postResult.regions.map(
      ({ name, external_id: externalID }) => ({
        option: name,
        value: externalID,
      })
    );

    absenteeFieldsStateUpdate.possibleRegions = possibleRegionsRemap;
  }

  store.dispatch({
    type: 'ABSENTEE_UPDATE_FIELDS',
    absenteeFields: absenteeFieldsStateUpdate,
  });

  const isStateFieldsEnabled = false;

  const esignEligible =
    (postResult.esign_method === 'leo_fax' ||
      postResult.esign_method === 'leo_email') &&
    isStateFieldsEnabled; // data.esign_method comes from turnout. Three options are self_print, leo_email, leo_fax. isStateFieldsEnabled comes from Optimizely.

  store.dispatch({
    type: 'ABSENTEE_INITIAL_ESIGN_ELIGIBILITY',
    initialEsignEligibility: esignEligible,
  });

  navigateToSecondStep();
}
